/**
 * VIEWFINDER（01 撮影）
 *
 * - MediaDevices API で iPhone カメラを起動（映像は object-fit: contain で全体を表示）
 * - プリセットのフォーマット比率で枠を表示。枠外を暗くし、三分割線と四隅のかぎ線
 * - 縦／横の切替（auto は端末の向きで決める）、カメラ前後の切替
 * - レンズの焦点距離に合わせたズーム（対応端末のみ）
 * - シャッター → 白いフラッシュ → 処理中 → 測光
 * - 位置情報（設定で on/off）
 * - カメラが使えないときは全画面の案内と「機材の選択に戻る」
 */

import { showScreen, goBack, toast, setLoading, escapeHtml, icon, buzz } from './ui.js';
import { getAspectRatio } from './presets.js';
import { calculateCenterCrop, captureFrameFromVideo, cropToBlob } from './crop.js';
import { calculateZoomFromPreset, getDeviceBaseFov, get35mmEquivFocal } from './data/film-formats.js';
import { getFilmById } from './data/films.js';
import { prefs } from './prefs.js';
import { formatLabel, FILTER_ABBR } from './gear.js';

let stream = null;
let activePreset = null;
let activeOrientation = null; // 'portrait' | 'landscape'
let useFrontCamera = false;
let zoomState = { applied: 1, target: 1, min: 1, max: 1, supported: false };

const VIDEO_CONSTRAINTS_BACK = {
  audio: false,
  video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1440 } },
};
const VIDEO_CONSTRAINTS_FRONT = {
  audio: false,
  video: { facingMode: { ideal: 'user' }, width: { ideal: 1280 }, height: { ideal: 960 } },
};

export async function openViewfinder(preset) {
  activePreset = preset;
  activeOrientation = resolveInitialOrientation(preset);
  useFrontCamera = false;
  render();
  showScreen('viewfinder');
  try {
    await startCamera();
    const video = document.getElementById('vf-video');
    video.addEventListener('loadedmetadata', updateFrame, { once: true });
    if (video.videoWidth) updateFrame();
  } catch (err) {
    console.error('[camera] failed to open:', err);
    renderError();
  }
}

export function closeViewfinder() {
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
  const video = document.getElementById('vf-video');
  if (video) video.srcObject = null;
}

function render() {
  const el = document.getElementById('screen-viewfinder');
  const film = getFilmById(activePreset.filmId);
  const abbr = FILTER_ABBR[activePreset.filterId];
  el.innerHTML = `
    <video id="vf-video" class="vf-video" playsinline muted autoplay></video>
    <div class="vf-frame" id="vf-frame">
      <span class="vf-frame__g" style="left:33.33%;top:0;bottom:0;width:1px"></span>
      <span class="vf-frame__g" style="left:66.66%;top:0;bottom:0;width:1px"></span>
      <span class="vf-frame__g" style="top:33.33%;left:0;right:0;height:1px"></span>
      <span class="vf-frame__g" style="top:66.66%;left:0;right:0;height:1px"></span>
      <span class="vf-frame__c" style="left:-2px;top:-2px;border-left-width:2px;border-top-width:2px"></span>
      <span class="vf-frame__c" style="right:-2px;top:-2px;border-right-width:2px;border-top-width:2px"></span>
      <span class="vf-frame__c" style="left:-2px;bottom:-2px;border-left-width:2px;border-bottom-width:2px"></span>
      <span class="vf-frame__c" style="right:-2px;bottom:-2px;border-right-width:2px;border-bottom-width:2px"></span>
    </div>
    <div class="vf-top">
      <button class="vf-top__btn" type="button" data-vf-close aria-label="閉じる">${icon('close', 24)}</button>
      <div class="vf-info">
        <div class="vf-info__a" id="vf-info"></div>
        <div class="vf-info__b">${escapeHtml([film?.name || activePreset.filmId, `EI ${activePreset.ei}`, abbr].filter(Boolean).join(' · '))}</div>
      </div>
      <div class="vf-gps ${prefs.gps ? '' : 'is-off'}" title="位置情報の記録">${icon('gps', 18)}<span>${prefs.gps ? 'GPS' : 'GPS OFF'}</span></div>
    </div>
    <div class="vf-zoom" id="vf-zoom" hidden><span></span></div>
    <div class="vf-ctrl">
      <button class="vf-side" type="button" data-vf-orient>
        <span class="vf-side__c"><span class="vf-ori" id="vf-ori"></span></span><span id="vf-ori-l"></span>
      </button>
      <button class="shutter" type="button" data-vf-shutter aria-label="撮影"><span></span></button>
      <button class="vf-side" type="button" data-vf-flip>
        <span class="vf-side__c">${icon('flip', 22)}</span><span id="vf-flip-l">前面カメラ</span>
      </button>
    </div>
  `;
  el.querySelector('[data-vf-close]').addEventListener('click', () => { closeViewfinder(); goBack(); });
  el.querySelector('[data-vf-shutter]').addEventListener('click', onShutter);
  el.querySelector('[data-vf-flip]').addEventListener('click', async () => {
    useFrontCamera = !useFrontCamera;
    document.getElementById('vf-flip-l').textContent = useFrontCamera ? '背面カメラ' : '前面カメラ';
    try { await startCamera(); updateFrame(); } catch { toast('カメラの切替に失敗しました'); }
    if (useFrontCamera) toast('前面カメラ：レンズに合わせたズームは適用されません');
  });
  el.querySelector('[data-vf-orient]').addEventListener('click', () => {
    activeOrientation = activeOrientation === 'portrait' ? 'landscape' : 'portrait';
    buzz(6);
    updateTopbar();
    updateFrame();
  });
  updateTopbar();
}

function renderError() {
  const el = document.getElementById('screen-viewfinder');
  el.innerHTML = `
    <div class="vf-error">
      <div class="eyebrow">camera</div>
      <div class="page-title">カメラを起動できませんでした</div>
      <p class="empty__d" style="margin-top:12px">ブラウザの設定でカメラの使用を許可してから、もう一度お試しください。iPhone では「設定 › Safari › カメラ」で確認できます。</p>
      <button class="btn-primary" type="button" data-vf-back style="margin-top:24px">機材の選択に戻る</button>
    </div>
  `;
  el.querySelector('[data-vf-back]').addEventListener('click', () => {
    closeViewfinder();
    // ホームから直接来た場合もあるので、機材の選択画面を開く
    import('./gear.js').then((m) => m.openSelectGear());
  });
}

// --------------------------------------------------
// カメラ
// --------------------------------------------------
async function startCamera() {
  closeViewfinder();
  stream = await navigator.mediaDevices.getUserMedia(useFrontCamera ? VIDEO_CONSTRAINTS_FRONT : VIDEO_CONSTRAINTS_BACK);
  const video = document.getElementById('vf-video');
  video.srcObject = stream;
  await video.play();
  if (!useFrontCamera) await applyPresetZoom();
  else { zoomState = { applied: 1, target: 1, min: 1, max: 1, supported: false }; updateZoomBadge(); }
}

/** プリセットの焦点距離 + フォーマットから必要なズーム倍率を出して適用 */
async function applyPresetZoom() {
  if (!stream || !activePreset) return;
  const track = stream.getVideoTracks()[0];
  if (!track) return;
  const targetZoom = calculateZoomFromPreset(activePreset, getDeviceBaseFov());
  zoomState.target = targetZoom;
  const caps = typeof track.getCapabilities === 'function' ? track.getCapabilities() : null;
  if (!caps || caps.zoom == null) {
    zoomState.supported = false;
    zoomState.applied = 1;
    updateZoomBadge();
    return;
  }
  zoomState.supported = true;
  zoomState.min = caps.zoom.min ?? 1;
  zoomState.max = caps.zoom.max ?? 1;
  const clamped = Math.max(zoomState.min, Math.min(zoomState.max, targetZoom));
  try {
    await track.applyConstraints({ advanced: [{ zoom: clamped }] });
    zoomState.applied = clamped;
  } catch (e) {
    console.warn('[zoom] applyConstraints failed:', e);
    zoomState.applied = 1;
  }
  updateZoomBadge();
}

function updateZoomBadge() {
  const wrap = document.getElementById('vf-zoom');
  if (!wrap) return;
  const focal = activePreset?.defaultLensFocalMm;
  if (!focal) { wrap.hidden = true; return; }
  wrap.hidden = false;
  const equiv = get35mmEquivFocal(activePreset.filmFormat, focal);
  const fmt = formatLabel(activePreset.filmFormat);
  let text = `${focal}mm @ ${fmt} ≈ ${equiv.toFixed(0)}mm`;
  if (useFrontCamera) text += ' · 前面カメラ';
  else if (zoomState.supported) {
    const reached = Math.abs(zoomState.applied - zoomState.target) < 0.05;
    text += ` · ${zoomState.applied.toFixed(2)}×${reached ? '' : `（最大 ${zoomState.max.toFixed(1)}×）`}`;
  } else text += ' · ズーム非対応';
  wrap.querySelector('span').textContent = text;
}

function resolveInitialOrientation(preset) {
  if (preset.defaultOrientation === 'portrait') return 'portrait';
  if (preset.defaultOrientation === 'landscape') return 'landscape';
  return (window.screen?.orientation?.type || '').includes('landscape') ? 'landscape' : 'portrait';
}

function updateTopbar() {
  const info = document.getElementById('vf-info');
  if (info) info.textContent = `${activePreset.camera || 'Camera'} · ${formatLabel(activePreset.filmFormat)} · ${activeOrientation === 'portrait' ? '縦' : '横'}`;
  const ori = document.getElementById('vf-ori');
  if (ori) {
    ori.style.width = activeOrientation === 'portrait' ? '14px' : '22px';
    ori.style.height = activeOrientation === 'portrait' ? '22px' : '14px';
  }
  const l = document.getElementById('vf-ori-l');
  if (l) l.textContent = activeOrientation === 'portrait' ? '横位置へ' : '縦位置へ';
  updateZoomBadge();
}

/**
 * 枠の描画。映像は contain で表示しているので、撮影時と同じ中央クロップを
 * 表示座標に写せば、枠の内側がそのまま記録される範囲になる。
 */
function updateFrame() {
  const video = document.getElementById('vf-video');
  const frame = document.getElementById('vf-frame');
  if (!video || !frame || !video.videoWidth) return;
  const rect = video.getBoundingClientRect();
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const scale = Math.min(rect.width / vw, rect.height / vh);
  const offX = (rect.width - vw * scale) / 2;
  const offY = (rect.height - vh * scale) / 2;
  const crop = calculateCenterCrop(vw, vh, getAspectRatio(activePreset, activeOrientation));
  frame.style.left = `${offX + crop.x * scale}px`;
  frame.style.top = `${offY + crop.y * scale}px`;
  frame.style.width = `${crop.w * scale}px`;
  frame.style.height = `${crop.h * scale}px`;
}

let resizeBound = false;
function bindResize() {
  if (resizeBound) return;
  resizeBound = true;
  window.addEventListener('resize', () => {
    if (document.getElementById('screen-viewfinder')?.classList.contains('screen--active')) updateFrame();
  });
  window.addEventListener('orientationchange', () => setTimeout(updateFrame, 200));
}
bindResize();

// --------------------------------------------------
// 撮影：元画像（全体）と中判フォーマットのクロップの両方を Blob にする
// --------------------------------------------------
async function onShutter() {
  const video = document.getElementById('vf-video');
  if (!video || !video.videoWidth) {
    toast('カメラが準備できていません');
    return;
  }
  buzz(20);
  const fx = document.createElement('div');
  fx.className = 'fx-flash';
  document.getElementById('app').appendChild(fx);
  setTimeout(() => fx.remove(), 520);

  try {
    const frame = await captureFrameFromVideo(video);
    const sourceW = video.videoWidth;
    const sourceH = video.videoHeight;
    setLoading('画像を処理しています', `CROPPING · ${formatLabel(activePreset.filmFormat)}`);

    const originalBlob = await cropToBlob(frame, { x: 0, y: 0, w: sourceW, h: sourceH }, sourceW, sourceH, 2400, 0.86);
    const cropBox = calculateCenterCrop(sourceW, sourceH, getAspectRatio(activePreset, activeOrientation));
    const croppedBlob = await cropToBlob(frame, cropBox, sourceW, sourceH);
    if (!originalBlob || !croppedBlob) throw new Error('blob conversion failed');

    // 位置情報（設定で OFF にできる）
    let gps = null;
    if (prefs.gps && 'geolocation' in navigator) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 });
        });
        gps = { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy };
      } catch (e) {
        console.info('[gps] not available:', e?.message);
      }
    }

    closeViewfinder();
    const { startShootFromCapture } = await import('./capture.js');
    await startShootFromCapture(activePreset, originalBlob, croppedBlob, {
      orientation: activeOrientation, sourceW, sourceH, cropBox, gps, capturedAt: Date.now(),
    });
  } catch (err) {
    console.error('[shutter] failed:', err);
    toast('撮影に失敗しました');
    showScreen('viewfinder', { anim: 'fade' });
  }
}

